/**
 * Plotex Engine — public entry point.
 *
 * `createPlotexEngine(options)` is the single factory that sets up the full
 * Three.js runtime: renderer, scene, camera, controls, lighting, GLTFLoader
 * (Draco + KTX2 + Meshopt + PlotexProgressiveExtension), environment, lightmaps,
 * progressive index, LOD manager, post-processing, and the animate loop.
 *
 * Consumers (main.ts, tests, embedding apps) call the factory once and get
 * back a PlotexEngine handle with the scene graph, controls, and a dispose()
 * method for teardown.
 */

import {
  ACESFilmicToneMapping,
  AmbientLight,
  Box3,
  Color,
  DirectionalLight,
  InstancedMesh,
  LinearSRGBColorSpace,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  PlaneGeometry,
  Quaternion,
  Scene,
  SRGBColorSpace,
  Sphere,
  type Texture,
  TextureLoader,
  Vector3,
  WebGLRenderer,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { VRButton } from 'three/examples/jsm/webxr/VRButton.js';
import { configureRendererShadows, configureSunShadow, enableShadowsOnScene } from './core/PlotexEngine';
import { PlotexEnvironmentLoader } from './core/PlotexEnvironmentLoader';
import { PlotexImpostorMaterial } from './core/PlotexImpostorMaterial';
import { PlotexLiveSync, attachLiveSync } from './core/PlotexLiveSync';
import { PlotexLodManager, type PlotexIndex } from './core/PlotexLodManager';
import { PlotexPostProcessing, type PostProcessPreset } from './core/PlotexPostProcessing';
import { PlotexCameraModeSwitcher } from './ui/PlotexCameraModeSwitcher';
import { PlotexMeasureTool } from './ui/PlotexMeasureTool';
import { PlotexSectionTool } from './ui/PlotexSectionTool';
import type { PlotexEngineOptions } from './types';
import { resolveOptions } from './types';

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const PROGRESSIVE_EXT = 'PLOTEX_progressive';
const MESH_REF_EXT = 'PLOTEX_mesh_ref';
const LIGHTMAP_EXT = 'PLOTEX_lightmap';

/* -------------------------------------------------------------------------- */
/* Internal types — engine internals, not part of the public API              */
/* -------------------------------------------------------------------------- */

/**
 * Per-mesh progressive-LOD metadata read from scene.glb's PLOTEX_progressive
 * extension. The runtime uses this to know which mesh_lod_*.glb file to
 * fetch when it wants to upgrade a mesh's geometry.
 */
type PlotexLodEntry = {
  path: string;
  level: number;
  vertexCount: number;
  indexCount: number;
  density: number;
};

type PlotexPriority = 'high' | 'normal' | 'low';

type PlotexProgressiveData = {
  guid: string;
  /** Level whose data is already baked into scene.glb for this mesh. */
  inlineLevel: number;
  /** Picker floor — never fetch a LOD coarser than this. 0 = no floor. */
  minLevel: number;
  /** Fetch-queue ordering hint. */
  priority: PlotexPriority;
  /**
   * P2b — optional: path to `mesh_<guid>.glb` when the Blender plugin
   * has produced a per-mesh chunk (via PLOTEX_mesh_ref). The LOD manager
   * prefers this for L0 fetches; falls back to the lods[] entry at level
   * 0 when not present. Allows either path to serve the full-quality
   * upgrade transparently.
   */
  baseMeshPath?: string;
  /**
   * P6 — path to the baked impostor atlas (relative to chunksPrefix),
   * written when the author flagged this mesh with
   * `PLOTEX_authoring.is_impostor_source`. `null` / `undefined` when no
   * impostor was baked. The impostor scan reads this to decide which
   * texture to load for the billboard quad.
   */
  impostorRef?: string | null;
  lods: PlotexLodEntry[];
};

/**
 * Per-object author intent stamped by the Blender plugin as
 * `node.extras.PLOTEX_authoring` (see PLOTEX_SCHEMA.md §2.1). Because the
 * plugin exports with `export_extras=True`, three.js's GLTFLoader copies
 * these fields straight into `mesh.userData.PLOTEX_authoring` — no custom
 * extension handler required. Only the fields the runtime currently
 * consumes are typed; everything else (target_texture_size, lightmap_resolution,
 * frustum_split…) is ignored at runtime and left for pipeline-side use.
 */
type PlotexAuthoringData = {
  type?: 'auto' | 'inline' | 'progressive' | 'impostor' | 'never_touch';
  lod_min?: number;
  never_decimate?: boolean;
  is_impostor_source?: boolean;
  priority?: PlotexPriority;
};

/**
 * P8 lightmap metadata captured from the root-level PLOTEX_lightmap
 * extension. When present, the post-load code fetches the atlas texture
 * and assigns it to every mesh's material.lightMap.
 */
type PlotexLightmapData = {
  path: string;
  size: number;
  uvChannel: number;
  uvLayer: string;
  bakeType: string;
  samples: number;
};

/* -------------------------------------------------------------------------- */
/* PlotexProgressiveExtension — GLTFLoader plugin                             */
/* -------------------------------------------------------------------------- */

/**
 * Three.js GLTFLoader plugin that captures the PLOTEX_progressive extension
 * from each mesh's JSON and stamps it onto the matching Three.Mesh objects'
 * `userData.__plotexProgressive`. We use the `afterRoot` hook because it
 * fires once the whole scene graph is built and `parser.associations` is
 * fully populated — that's the map we use to turn a glTF mesh index into
 * the actual Three.Mesh objects (one mesh index can map to multiple Meshes
 * when a mesh has multiple primitives or is instanced from multiple nodes).
 */
class PlotexProgressiveExtension {
  parser: any;
  name = PROGRESSIVE_EXT;
  // Captured lightmap data, read by the engine after scene load completes.
  static lastLightmapData: PlotexLightmapData | null = null;

  constructor(parser: any) {
    this.parser = parser;
  }

  afterRoot(_result: any): null {
    const json = this.parser.json;
    if (!json?.meshes) return null;

    // Walk parser.associations (Map<Object3D, {meshes?, primitives?, nodes?}>)
    // to invert it: meshIndex -> Three.Mesh[]. associations is populated by
    // GLTFParser._invokeAll('createNodeMesh') so it is ready by afterRoot.
    //
    // Two shapes to handle:
    //   - Regular Mesh:      info.meshes = <gltf mesh index>
    //   - InstancedMesh:     info.nodes  = <gltf node index>, NO `meshes` key
    //                        (EXT_mesh_gpu_instancing wraps mesh creation)
    //     We resolve node.mesh from json.nodes[nodeIdx] in that case.
    const meshIndexToObjects = new Map<number, Mesh[]>();
    const associations: Map<any, any> = this.parser.associations ?? new Map();
    for (const [obj, info] of associations) {
      if (!(obj as any)?.isMesh) continue;
      let idx: number | undefined = info?.meshes;
      // InstancedMesh path: look up the mesh index from the node
      if (idx === undefined && typeof info?.nodes === 'number') {
        idx = json.nodes?.[info.nodes]?.mesh;
      }
      if (idx === undefined) continue;
      let list = meshIndexToObjects.get(idx);
      if (!list) {
        list = [];
        meshIndexToObjects.set(idx, list);
      }
      list.push(obj as Mesh);
    }

    // P2b: Build a guid -> base_path map by walking nodes for PLOTEX_mesh_ref.
    // This lets us prefer per-mesh chunk files over the lods[].path entry at
    // level 0 when the Blender plugin has emitted them (mesh_<guid>.glb).
    const basePathByGuid = new Map<string, string>();
    for (const node of json.nodes ?? []) {
      const ref = node?.extensions?.[MESH_REF_EXT];
      if (ref?.guid && ref?.base_path) {
        basePathByGuid.set(ref.guid, ref.base_path);
      }
    }

    let stamped = 0;
    let baseHits = 0;
    for (let i = 0; i < json.meshes.length; i++) {
      const ext = json.meshes[i]?.extensions?.[PROGRESSIVE_EXT];
      if (!ext) continue;
      const targets = meshIndexToObjects.get(i);
      if (!targets) continue;
      const baseMeshPath = ext.guid ? basePathByGuid.get(ext.guid) : undefined;
      if (baseMeshPath) baseHits++;
      // impostorRef is stamped on PLOTEX_progressive by the plugin's P6
      // bake step. We lift it onto the same userData payload so the
      // impostor scan in createPlotexEngine() doesn't have to poke at
      // parser internals — everything it needs is on the mesh itself.
      const impostorRef: string | null | undefined = (ext as any).impostorRef;
      const data: PlotexProgressiveData = {
        ...(ext as PlotexProgressiveData),
        baseMeshPath,
        impostorRef,
      };
      for (const m of targets) {
        m.userData.__plotexProgressive = data;
        stamped++;
      }
    }
    console.log(
      `[t5] Stamped ${PROGRESSIVE_EXT} on ${stamped} Three.Mesh objects` +
        (baseHits > 0 ? ` (${baseHits} carry PLOTEX_mesh_ref base path)` : '')
    );

    // P8: root-level PLOTEX_lightmap carries the shared atlas path. We
    // capture it here; the engine applies it to all materials after the
    // scene is added.
    const lm = json?.extensions?.[LIGHTMAP_EXT];
    if (lm?.path) {
      PlotexProgressiveExtension.lastLightmapData = lm as PlotexLightmapData;
      console.log(`[t5] ${LIGHTMAP_EXT}: ${lm.path} (${lm.size}²)`);
    }

    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* PlotexEngine — the handle returned by the factory                          */
/* -------------------------------------------------------------------------- */

/**
 * One impostor/real pair tracked by the runtime swap logic. Built once
 * per `is_impostor_source` mesh at load time, iterated every frame to
 * toggle visibility based on camera distance. Exposed on the engine
 * handle so it can be inspected from DevTools (`window.__plotex.impostorPairs`).
 */
export interface PlotexImpostorPair {
  /** The original, full-geometry object that hides when the camera is far. */
  real: Object3D;
  /** The camera-facing billboard quad that shows when the camera is far. */
  impostor: InstancedMesh;
  /** Center distance at which the swap happens, in world meters. */
  switchDistance: number;
  /** Cluster centroid — kept for debugging, not used in the per-frame swap. */
  worldCenter: Vector3;
  /** Per-instance world positions. The per-frame swap loop computes the
   *  min distance across these so a camera standing next to any one
   *  palm keeps the full real geometry visible even if the cluster's
   *  CENTROID is past the switch distance. */
  instancePositions: ReadonlyArray<Vector3>;
}

export interface PlotexEngine {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  controls: OrbitControls;
  lodManager: PlotexLodManager;
  postProcessing: PlotexPostProcessing;
  /** Real/impostor pairs created by the impostor scan. Empty when no author opted in. */
  impostorPairs?: ReadonlyArray<PlotexImpostorPair>;
  /**
   * Click-to-measure tool. Built when `options.measureTool.enabled !== false`
   * (default). Inactive by default — toggle via the bottom-right toolbar
   * button or programmatically via `measureTool.activate()`. `undefined` when
   * the tool was disabled in options.
   */
  measureTool?: PlotexMeasureTool;
  /**
   * Orbit/Walk camera mode toggle. Owns the OrbitControls instance above
   * AND a PointerLockControls for first-person WASD navigation; flips
   * `controls.enabled` and pointer-lock state when the user clicks the
   * toolbar button. Present whenever the engine was built (there's no
   * opt-out in options — the Walk button is always available).
   */
  cameraModeSwitcher?: PlotexCameraModeSwitcher;
  /**
   * Section / cutaway clipping plane tool. Enabled by default; opts out
   * via `options.sectionTool.enabled = false`. The tool sets
   * `renderer.localClippingEnabled = true` on construction and pushes /
   * pops its plane from `renderer.clippingPlanes` on activate / deactivate.
   * Can be active simultaneously with the measure tool — both are
   * independent toolbar affordances.
   */
  sectionTool?: PlotexSectionTool;
  /** Tear down the engine: stop the animate loop, dispose workers, release GPU. */
  dispose(): void;
  /** Switch the post-processing quality preset at runtime. */
  setPostProcessPreset(preset: PostProcessPreset): void;
}

/* -------------------------------------------------------------------------- */
/* createPlotexEngine — the factory                                           */
/* -------------------------------------------------------------------------- */

/**
 * Create and start a Plotex engine instance. This is the main entry point
 * for embedding the viewer — call it once with a canvas and options, and
 * it returns a PlotexEngine handle for runtime control + teardown.
 *
 * The factory is async because it loads scene.glb (Draco/KTX2 decode),
 * optionally loads the HDRI environment, and applies lightmaps before the
 * first frame renders.
 */
export async function createPlotexEngine(
  opts: PlotexEngineOptions,
): Promise<PlotexEngine> {
  const cfg = resolveOptions(opts);
  const canvas = cfg.canvas;

  // ---- Renderer ----------------------------------------------------------
  const renderer = new WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: 'high-performance',
  });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;

  // [plotex-hook:xr] — opt into WebXR. This flag alone makes
  // `renderer.setAnimationLoop(fn)` route through the XR frame callback
  // when a session is active; without it the VRButton can't start an
  // immersive session at all. Safe to enable unconditionally even on
  // browsers without WebXR — the three.js XR manager just stays dormant
  // until an actual session handshake happens.
  const xrEnabled = cfg.xr?.enabled !== false;
  if (xrEnabled) {
    renderer.xr.enabled = true;
  }
  // [/plotex-hook:xr]

  // [plotex-hook:shadows] — enable shadow maps at renderer init.
  // configureSunShadow + enableShadowsOnScene run later (they need the scene
  // bounding box from the loaded GLB), but the renderer-level toggle must
  // happen before the first render pass.
  if (cfg.shadows?.enabled !== false) {
    configureRendererShadows(renderer);
  }
  // [/plotex-hook:shadows]

  // ---- Scene -------------------------------------------------------------
  const scene = new Scene();
  scene.background = new Color(cfg.environment?.fallbackColor ?? 0x1a1c22);

  const camCfg = cfg.camera!;
  const camera = new PerspectiveCamera(
    camCfg.fov!,
    window.innerWidth / window.innerHeight,
    camCfg.near!,
    camCfg.far!,
  );
  const initPos = camCfg.initialPosition!;
  camera.position.set(initPos[0], initPos[1], initPos[2]);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;

  // ---- Camera mode switcher (Orbit <-> Walk) -----------------------------
  // Owns OrbitControls + a parallel PointerLockControls, and surfaces a
  // toolbar button stacked above the measure tool. We build it here (right
  // after OrbitControls) so the controls reference stays local and the
  // button lands in the DOM at the same lifecycle stage as the other
  // widgets. `cfg.camera.controls` drives the initial mode — 'walk' boots
  // straight into FPS on first frame (the button click we'd normally need
  // for pointer lock still happens later when the user clicks into the
  // canvas; the browser will prompt for lock then).
  const cameraModeSwitcher = new PlotexCameraModeSwitcher({
    camera,
    controls,
    canvas,
    speed: camCfg.walkSpeed,
    sprintSpeed: camCfg.walkSprintSpeed,
  });
  if (camCfg.controls === 'walk') {
    cameraModeSwitcher.setMode('walk');
  } else if (camCfg.controls === 'none') {
    // Explicit 'none' disables both controls. The switcher button still
    // appears so the user can re-enable interaction at their discretion.
    controls.enabled = false;
  }

  // ---- Lighting ----------------------------------------------------------
  scene.add(new AmbientLight(0xffffff, 0.7));
  const sun = new DirectionalLight(0xffffff, 1.8);
  sun.position.set(120, 180, 80);
  scene.add(sun);

  // ---- Load the GLB ------------------------------------------------------
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  // T5: register the PLOTEX_progressive capture extension before loading.
  PlotexProgressiveExtension.lastLightmapData = null;
  loader.register((parser) => new PlotexProgressiveExtension(parser));

  // T3: scene.glb is Draco-compressed. We host the decoder ourselves
  // (copied from three/examples/jsm/libs/draco/gltf/) so there is no CDN
  // dependency and the runtime stays fully offline-capable.
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath(cfg.decoderPaths?.draco ?? '/draco/');
  dracoLoader.setDecoderConfig({ type: 'wasm' });
  loader.setDRACOLoader(dracoLoader);

  // T16: scene.glb textures are now KTX2/BasisU (KHR_texture_basisu).
  // Register the KTX2Loader with its local transcoder and detect GPU
  // support for compressed texture formats on this machine.
  const ktx2Loader = new KTX2Loader();
  ktx2Loader.setTranscoderPath(cfg.decoderPaths?.basis ?? '/basis/');
  ktx2Loader.detectSupport(renderer);
  loader.setKTX2Loader(ktx2Loader);

  // Wire the onProgress callback through to GLTFLoader. three.js forwards
  // XHR progress events as {loaded, total}; we normalize to a [0, 1]
  // ratio so the host's loading bar doesn't care whether the CDN sent
  // Content-Length. When the server sends chunked transfer without a
  // length header, lengthComputable is false and the ratio stays 0 —
  // which is the right signal for the host to show an indeterminate
  // spinner instead of a misleading "100%".
  const loaded = await loader.loadAsync(cfg.sceneUrl, (xhr: ProgressEvent) => {
    const onProgress = opts.onProgress;
    if (!onProgress) return;
    let ratio = 0;
    if (xhr.lengthComputable && xhr.total > 0) {
      ratio = Math.min(1, xhr.loaded / xhr.total);
    }
    try {
      onProgress(ratio);
    } catch (err) {
      // A broken host callback should never take down the engine load.
      console.warn('[plotex] onProgress callback threw:', err);
    }
  });
  scene.add(loaded.scene);

  // [plotex-hook:environment] — HDRI IBL via PlotexEnvironmentLoader.
  // Reads the PLOTEX_environment root extension (stamped by the plugin's
  // environment bake op), loads the KTX2 cube via PMREM, and assigns the
  // prefiltered texture to scene.environment so three.js PBR materials
  // have real reflections instead of flat DirectionalLight shading.
  const envExt = (loaded as any)?.parser?.json?.extensions?.['PLOTEX_environment'];
  const hdrPath = cfg.environment?.hdrPath;
  const envSrc = hdrPath ?? (envExt?.path ? cfg.chunksPrefix + envExt.path : null);
  if (envSrc) {
    try {
      const envLoader = new PlotexEnvironmentLoader(renderer, ktx2Loader);
      const envTex = await envLoader.load(envSrc);
      scene.environment = envTex;
      console.log(`[plotex] loaded HDRI environment: ${envSrc}`);
    } catch (err) {
      console.warn('[plotex] HDRI environment load failed, using fallback lighting:', err);
    }
  }
  // [/plotex-hook:environment]

  // [plotex-hook:impostor] — runtime impostor swap (M3 closer).
  //
  // For every mesh the Blender plugin flagged with
  // `PLOTEX_authoring.is_impostor_source`, the plugin baked an 8-view
  // billboard atlas (`impostor_<guid>.png` or `.ktx2`). This block walks
  // the freshly-loaded scene graph, builds a camera-facing quad per
  // flagged mesh, loads the matching atlas, and parks both the real and
  // the impostor side-by-side in an `impostorPairs` array. The per-frame
  // update further down in animate() flips visibility + crossfades
  // opacity as the camera crosses `switchDistance`.
  //
  // Author intent arrives via `node.extras.PLOTEX_authoring` — three.js's
  // GLTFLoader auto-copies `node.extras` fields into `mesh.userData`
  // (see assignExtrasToUserData in GLTFLoader.js), so no custom extension
  // handler is needed for capture. The atlas path comes from
  // PLOTEX_progressive.impostorRef, which is captured into
  // `mesh.userData.__plotexProgressive.impostorRef` by our extension.
  //
  // If the scene has zero impostor sources, we leave `impostorPairs`
  // empty AND skip the per-frame update pass entirely — no wasted
  // traversal work for scenes that don't opt in.
  const impostorPairs: PlotexImpostorPair[] = [];
  if (cfg.impostor?.enabled !== false) {
    // First pass: collect candidate real meshes. We DON'T create
    // impostors inside traverse() because the atlas load is async —
    // building them later lets us HEAD-check ktx2 vs png once per
    // unique guid and await all texture loads in parallel.
    //
    // For InstancedMesh reals (palms, fences, bollards — anything that was
    // merged into an instance group by the pipeline) we must produce ONE
    // impostor PER INSTANCE, not one big quad covering the whole cluster.
    // The original bug: `Box3.setFromObject(instancedMesh)` returns the
    // AABB of the entire instance group, so `radius` was the spread of
    // the cluster (hundreds of meters) and the impostor rendered as one
    // giant smear across the row of 52 palms. The fix uses the local
    // geometry bbox (ONE instance's size) for the quad, and per-instance
    // world matrices so each billboard lands on a real palm position.
    type ImpostorCandidate = {
      real: Object3D;
      guid: string;
      impostorRef?: string | null;
      /** Per-billboard world positions; one entry per impostor instance. */
      instancePositions: Vector3[];
      /** Half the diameter of ONE instance (not the cluster). */
      instanceRadius: number;
      /** Cluster centroid used by the per-frame swap logic for a cheap
       *  single-distance test. Falls out as the average of instancePositions. */
      worldCenter: Vector3;
    };
    const candidates: ImpostorCandidate[] = [];
    const tmpBox = new Box3();
    const tmpSphere = new Sphere();
    const tmpMatrix = new Matrix4();
    const tmpQuat = new Quaternion();
    loaded.scene.updateMatrixWorld(true);
    loaded.scene.traverse((obj: any) => {
      if (!obj.isMesh) return;
      const authoring = obj.userData?.PLOTEX_authoring as PlotexAuthoringData | undefined;
      if (!authoring?.is_impostor_source) return;
      // Pull guid + impostorRef off the progressive payload that our
      // extension already stamped. If the mesh has no progressive data
      // (author flagged is_impostor_source on something the pipeline
      // inlined), we still create a best-effort impostor using the
      // material guid fallback — but a missing guid means no atlas
      // path and we have to skip.
      const prog = obj.userData?.__plotexProgressive as
        | PlotexProgressiveData
        | undefined;
      const guid = prog?.guid;
      if (!guid) {
        console.warn(
          '[plotex-impostor] skipping impostor source — no guid on',
          obj.name,
        );
        return;
      }

      // Per-instance radius: compute from the MESH's own bounding
      // sphere (local-space), scaled by the mesh's world scale. This
      // represents the extent of ONE palm, not the row.
      if (!obj.geometry?.boundingSphere) obj.geometry?.computeBoundingSphere?.();
      const localRadius = obj.geometry?.boundingSphere?.radius ?? 1;
      const parentWorldScale = new Vector3();
      obj.matrixWorld.decompose(new Vector3(), tmpQuat, parentWorldScale);
      // Instance scale varies per-instance; we take the max across all
      // instances so the quad never ends up smaller than any single
      // billboard. Starts at 0 — the loop will set it to the actual
      // max scale (which for palm-tree clusters is ~0.02, because the
      // source mesh lives in a large local coord frame and each
      // instance matrix carries a tiny compensating scale; see the
      // CLAUDE.md gotcha #10 for the full story). For non-instanced
      // meshes we fall back to 1.0 after the loop.
      let maxInstanceScale = 0;
      const positions: Vector3[] = [];

      if (obj.isInstancedMesh) {
        const im = obj as InstancedMesh;
        const tmpT = new Vector3();
        const tmpS = new Vector3();
        for (let i = 0; i < im.count; i++) {
          im.getMatrixAt(i, tmpMatrix);
          // Compose world_T = mesh.matrixWorld * instanceMatrix.
          // Apply to origin to get per-instance world position.
          const worldPos = new Vector3(0, 0, 0)
            .applyMatrix4(tmpMatrix)
            .applyMatrix4(obj.matrixWorld);
          positions.push(worldPos);
          tmpMatrix.decompose(tmpT, tmpQuat, tmpS);
          const s = Math.max(tmpS.x, tmpS.y, tmpS.z);
          if (s > maxInstanceScale) maxInstanceScale = s;
        }
      } else {
        // Plain mesh — single billboard at its world origin.
        tmpBox.makeEmpty();
        tmpBox.setFromObject(obj);
        if (tmpBox.isEmpty()) return;
        tmpBox.getBoundingSphere(tmpSphere);
        positions.push(tmpSphere.center.clone());
        // Non-instanced meshes don't have per-instance scales — fall
        // back to 1.0 so the diameter math below stays stable.
        maxInstanceScale = 1;
      }

      // Safety net: if the loop ran but every scale was zero (degenerate
      // instance matrix), clamp to something sensible so we don't emit
      // a zero-size quad.
      if (maxInstanceScale === 0) maxInstanceScale = 1;

      if (positions.length === 0) return;

      // Cluster centroid for the coarse per-frame distance test. We
      // use the average of instance positions — the per-frame loop does
      // ONE distance check per impostor group, and swaps ALL instances
      // of a group together. Individual per-instance distance checks
      // would let the row of palms swap in progressively, but that
      // visibly shifts the LOD boundary across the scene and looks odd;
      // group-swap is simpler and matches how the eye reads a cluster.
      const cx = positions.reduce((a, p) => a + p.x, 0) / positions.length;
      const cy = positions.reduce((a, p) => a + p.y, 0) / positions.length;
      const cz = positions.reduce((a, p) => a + p.z, 0) / positions.length;

      const parentScale = Math.max(parentWorldScale.x, parentWorldScale.y, parentWorldScale.z) || 1;

      candidates.push({
        real: obj,
        guid,
        impostorRef: prog?.impostorRef,
        instancePositions: positions,
        instanceRadius: localRadius * maxInstanceScale * parentScale,
        worldCenter: new Vector3(cx, cy, cz),
      });
    });

    if (candidates.length > 0) {
      // Async atlas fetch. We await this block so the returned engine
      // has its impostor set fully wired before the first frame renders
      // — otherwise the real meshes would flicker through their quads
      // for a few frames while textures stream in.
      const switchDistance = cfg.impostor?.switchDistance ?? 50;

      await Promise.all(
        candidates.map(async (c) => {
          // Resolve the atlas URL. Preferred order:
          //   1. Explicit impostorRef from progressive metadata.
          //      Accepts "impostor_<guid>.png" (plugin raw) or the
          //      pipeline-transcoded .ktx2 (both siblings to chunks).
          //   2. Derived `/chunks/impostor_<guid>.{ktx2|png}` via HEAD
          //      probe — ktx2 first because it's GPU-native.
          let atlasUrl: string | null = null;
          let useKtx2 = false;

          if (c.impostorRef) {
            const base = c.impostorRef.replace(/\.(png|ktx2)$/i, '');
            const ktx2Url = cfg.chunksPrefix + base + '.ktx2';
            const pngUrl = cfg.chunksPrefix + base + '.png';
            // HEAD-check ktx2 first; fall through to png on 404.
            try {
              const r = await fetch(ktx2Url, { method: 'HEAD' });
              if (r.ok) {
                atlasUrl = ktx2Url;
                useKtx2 = true;
              }
            } catch {
              // network error — try png below
            }
            if (!atlasUrl) {
              try {
                const r = await fetch(pngUrl, { method: 'HEAD' });
                if (r.ok) atlasUrl = pngUrl;
              } catch {
                // both probes failed, atlasUrl stays null
              }
            }
          } else {
            // Metadata didn't carry an explicit ref — probe the
            // conventional paths based on guid alone.
            const ktx2Url = `${cfg.chunksPrefix}impostor_${c.guid}.ktx2`;
            const pngUrl = `${cfg.chunksPrefix}impostor_${c.guid}.png`;
            try {
              const r = await fetch(ktx2Url, { method: 'HEAD' });
              if (r.ok) {
                atlasUrl = ktx2Url;
                useKtx2 = true;
              }
            } catch {
              // ignore
            }
            if (!atlasUrl) {
              try {
                const r = await fetch(pngUrl, { method: 'HEAD' });
                if (r.ok) atlasUrl = pngUrl;
              } catch {
                // ignore
              }
            }
          }

          if (!atlasUrl) {
            console.warn(
              `[plotex-impostor] no atlas found for ${c.real.name} (guid ${c.guid.slice(0, 8)}), skipping`,
            );
            return;
          }

          // Load the texture. KTX2Loader returns a CompressedTexture
          // usable on the GPU; TextureLoader hands back a normal
          // sRGB Texture. The shader treats both identically since it
          // just reads sRGB RGBA samples.
          let atlas: Texture;
          try {
            atlas = useKtx2
              ? await ktx2Loader.loadAsync(atlasUrl)
              : await new TextureLoader().loadAsync(atlasUrl);
          } catch (err) {
            console.warn(
              `[plotex-impostor] failed to load atlas ${atlasUrl}:`,
              err,
            );
            return;
          }
          // The atlas is authored in sRGB color space (Blender renders
          // to 8-bit PNGs with sRGB encoding by default); declare it so
          // three.js linearises on sample. flipY=false to match glTF
          // convention used elsewhere in the pipeline.
          atlas.colorSpace = SRGBColorSpace;
          atlas.flipY = false;

          // Build the billboard quad sized to ONE real instance. The
          // earlier implementation fed the whole InstancedMesh's
          // group-bbox radius into PlaneGeometry, which produced a
          // single mega-quad spanning the entire row of 52 palms and
          // rendered as a horizontal smear. Using `instanceRadius`
          // (the per-instance extent) plus per-instance world matrices
          // below matches the real mesh's layout.
          const diameter = Math.max(0.1, c.instanceRadius * 2);
          const geom = new PlaneGeometry(diameter, diameter);
          const material = PlotexImpostorMaterial.createFor(atlas);
          // Opt into transparency so crossfadeBand can animate opacity
          // without leaving a hard cut-in at the band boundary. The
          // fragment shader still discards fully-transparent texels,
          // but enabling transparent=true lets the remaining translucent
          // pixels blend into the framebuffer during the fade.
          material.transparent = true;
          material.depthWrite = false;

          // One impostor InstancedMesh carrying N billboard instances —
          // one per matching real instance. The vertex shader reads
          // per-instance `instanceMatrix` to place the quad at each
          // tree's location, then does billboard rotation around Y
          // based on the camera azimuth (see PlotexImpostorMaterial).
          const impostor = new InstancedMesh(geom, material, c.instancePositions.length);
          for (let i = 0; i < c.instancePositions.length; i++) {
            const p = c.instancePositions[i];
            const m = new Matrix4().makeTranslation(p.x, p.y, p.z);
            impostor.setMatrixAt(i, m);
          }
          impostor.instanceMatrix.needsUpdate = true;
          impostor.name = `plotex-impostor:${c.real.name}`;
          // Hidden by default — the per-frame pass flips this once it
          // measures the camera distance. Starting hidden means the
          // first rendered frame always shows real geometry, which is
          // the right default (impostors exist to save FPS, not replace
          // geometry that's already close to the camera).
          impostor.visible = false;
          impostor.frustumCulled = true;
          scene.add(impostor);

          impostorPairs.push({
            real: c.real,
            impostor,
            switchDistance,
            worldCenter: c.worldCenter,
            instancePositions: c.instancePositions,
          });
        }),
      );

      console.log(
        `[plotex-impostor] wired ${impostorPairs.length} impostor pair(s) ` +
          `(switchDistance=${switchDistance}m, ` +
          `crossfadeBand=${cfg.impostor?.crossfadeBand ?? 0.5}m)`,
      );
    }
  }
  // [/plotex-hook:impostor]

  // ---- P8 / Wave 5 Task 2D: apply AO lightmap atlas to materials ---------
  // If scene.glb carried a PLOTEX_lightmap extension, fetch the atlas from
  // `${chunksPrefix}${path}` and assign it as an AO map on every eligible
  // material. Three.js samples the AO map via the second UV channel —
  // glTF's TEXCOORD_1 → geometry.attributes.uv1 in three.js's naming.
  //
  // Why aoMap (not lightMap): the plugin bakes pure ambient occlusion.
  // aoMap MULTIPLIES base color (darkens crevices correctly under any
  // lighting), while lightMap ADDS emissive light (doesn't respect the
  // direct-lighting term, so shadowed areas look too bright). This is
  // the correction the master plan calls out in Task 2D.
  if (PlotexProgressiveExtension.lastLightmapData) {
    // Capture into a block-scoped const so TS narrows past the awaits below.
    // The static is set synchronously in afterRoot before this code runs.
    const lmData: PlotexLightmapData = PlotexProgressiveExtension.lastLightmapData;
    try {
      const ktxUrl = cfg.chunksPrefix + lmData.path.replace('.png', '.ktx2');
      const pngUrl = cfg.chunksPrefix + lmData.path;
      // Try KTX2 first (pipeline-compressed), fall back to PNG (raw plugin output).
      const res = await fetch(ktxUrl, { method: 'HEAD' });
      const useKtx = res.ok;
      const texUrl = useKtx ? ktxUrl : pngUrl;
      const tex = useKtx
        ? await ktx2Loader.loadAsync(texUrl)
        : await new TextureLoader().loadAsync(texUrl);
      // Lightmaps go in LINEAR color space (they're physical light data,
      // not sRGB-encoded color). Texture flipY matches three.js's glTF
      // convention (non-flipped).
      tex.colorSpace = LinearSRGBColorSpace;
      tex.flipY = false;
      let appliedCount = 0;
      let skippedNoUv = 0;
      let skippedBadMat = 0;
      loaded.scene.traverse((obj: any) => {
        if (!obj.isMesh) return;
        const geom = obj.geometry;
        const mat = obj.material;
        // AO map requires the second UV set. Without uv1 the shader has
        // nowhere to sample the atlas, so we skip rather than show a
        // broken tiled AO texture across the mesh.
        if (!geom?.attributes?.uv1) {
          skippedNoUv++;
          return;
        }
        if (!(mat instanceof MeshStandardMaterial)) {
          skippedBadMat++;
          return;
        }
        mat.aoMap = tex;
        mat.aoMapIntensity = 1.0;
        mat.needsUpdate = true;
        appliedCount++;
      });
      console.log(
        `[p8] AO lightmap applied to ${appliedCount} materials ` +
        `(source: ${useKtx ? 'KTX2' : 'PNG'}, ` +
        `skipped: ${skippedNoUv} no-uv1 + ${skippedBadMat} non-standard-mat)`
      );
    } catch (err) {
      console.warn('[p8] lightmap load failed:', err);
    }
  }

  // ---- T5: build the PLOTEX_progressive index ----------------------------
  // Walk the loaded scene, collect every Three.Mesh that the extension
  // plugin stamped with progressive metadata. Sort each mesh's LOD list
  // by level (low -> high quality) so consumers can binary-search later.
  const plotexIndex: PlotexIndex = new Map();
  loaded.scene.traverse((obj: any) => {
    if (!obj.isMesh) return;
    const data = obj.userData?.__plotexProgressive as
      | PlotexProgressiveData
      | undefined;
    if (!data) return;
    // Defensive copy + sort by level so the runtime can rely on order.
    const sortedLods = [...data.lods].sort((a, b) => a.level - b.level);
    const pri = (data.priority as PlotexPriority | undefined) ?? 'normal';
    plotexIndex.set(obj as Mesh, {
      guid: data.guid,
      inlineLevel: typeof data.inlineLevel === 'number' ? data.inlineLevel : -1,
      minLevel: typeof data.minLevel === 'number' ? data.minLevel : 0,
      priority: pri === 'high' || pri === 'normal' || pri === 'low' ? pri : 'normal',
      baseMeshPath: data.baseMeshPath,
      lods: sortedLods,
    });
  });
  console.log(
    `[t5] Indexed ${plotexIndex.size} progressive meshes`,
    plotexIndex.size > 0
      ? `(first guid: ${[...plotexIndex.values()][0].guid}, ${[...plotexIndex.values()][0].lods.length} LODs)`
      : ''
  );

  // ---- Frame the camera on the loaded bounds -----------------------------
  // Skip "ground" meshes when computing bounds — they're huge and wreck
  // the framing math. Same heuristic Needle uses internally.
  const bounds = new Box3();
  loaded.scene.traverse((obj: any) => {
    if (!obj.isMesh) return;
    if (/^(ground|terrain|plaza)/i.test(obj.name)) return;
    const pos = obj.geometry?.attributes?.position;
    if (!pos || pos.count <= 4) return;
    bounds.union(new Box3().setFromObject(obj));
  });

  if (!bounds.isEmpty()) {
    const center = new Vector3();
    const size = new Vector3();
    bounds.getCenter(center);
    bounds.getSize(size);
    const radius = Math.max(size.x, size.z) * 0.9;
    camera.position.set(
      center.x + radius * 0.7,
      center.y + radius * 0.5,
      center.z + radius * 0.7
    );
    controls.target.copy(center);
    controls.update();

    // Auto-expand near/far to fit the scene. Our default far=5000 works
    // for the BB2 reference (~500 m diagonal) but the moment someone
    // exports a masterplan / terrain / game-map scene (tens of km) the
    // camera gets placed outside the frustum and the viewport goes
    // black. We keep the configured value as a floor so small scenes
    // don't pay a depth-precision tax, and push the far plane out to
    // 5× the scene diagonal from the new camera position — enough to
    // cover orbiting around the far side without clipping.
    const diag = size.length();
    const camDist = camera.position.distanceTo(center);
    camera.far = Math.max(cfg.camera!.far!, (camDist + diag) * 2);
    camera.near = Math.max(0.1, diag / 100000);
    camera.updateProjectionMatrix();
  }

  // Shadow frustum + per-mesh flags, deferred from [plotex-hook:shadows] to
  // here because the sun frustum needs the scene bounds, and the scene walk
  // must happen after loaded.scene is in the graph.
  if (!bounds.isEmpty() && cfg.shadows?.enabled !== false) {
    configureSunShadow(sun, bounds, {
      mapSize: cfg.shadows?.mapSize,
      bias: cfg.shadows?.bias,
    });
    enableShadowsOnScene(loaded.scene);
  }

  // ---- Wave 5 Task 1E: PLOTEX_project runtime extension ------------------
  // The plugin stamps runtime-relevant tunables (tris/pixel target, FPS
  // floor, fetch concurrency, update cadence, postprocess preset) onto
  // scene.glb as a root-level PLOTEX_project extension. We read it once
  // here and apply the values to the LOD manager + post-processing
  // pipeline. Missing extension = use built-in defaults; the runtime is
  // backward-compatible with scene.glb files produced before 1E shipped.
  const projectExt = (loaded as any)?.parser?.json?.extensions?.['PLOTEX_project'];

  // ---- LOD manager -------------------------------------------------------
  const poolSize = cfg.lod?.poolSize ??
    Math.min(4, Math.max(1, (navigator.hardwareConcurrency ?? 4) - 2));
  const lodManager = new PlotexLodManager(plotexIndex, poolSize);

  // Apply LOD tunables: project extension first (author intent), then
  // explicit options overrides (embedding-app override), so the options
  // object always wins when both are present.
  const lodTunables = {
    updateIntervalMs:
      cfg.lod?.updateIntervalMs ??
      (typeof projectExt?.update_interval_ms === 'number' ? projectExt.update_interval_ms : undefined),
    maxInflight:
      cfg.lod?.maxInflight ??
      (typeof projectExt?.max_inflight === 'number' ? projectExt.max_inflight : undefined),
    fpsTarget:
      cfg.lod?.fpsTarget ??
      (typeof projectExt?.fps_target === 'number' ? projectExt.fps_target : undefined),
    trisPerPixelTarget:
      cfg.lod?.trisPerPixelTarget ??
      (typeof projectExt?.tris_per_pixel_target === 'number' ? projectExt.tris_per_pixel_target : undefined),
    minScreenRadiusPx:
      cfg.lod?.minScreenRadiusPx ??
      (typeof projectExt?.min_screen_radius_px === 'number' ? projectExt.min_screen_radius_px : undefined),
  };
  const hasAnyTunable = Object.values(lodTunables).some((v) => v !== undefined);
  if (hasAnyTunable) {
    lodManager.configure(lodTunables);
    if (projectExt) {
      console.log('[plotex] applied project config:', {
        preset: projectExt.preset,
        ...lodTunables,
      });
    }
  }

  // ---- Measure tool ------------------------------------------------------
  // Standard arch-viz affordance: click two points to see the distance
  // between them. We construct it right after the LOD manager so the tool
  // can hook into the same scene graph the LOD manager is picking LODs
  // against — raycasting runs against `loaded.scene` specifically (not the
  // full three scene) so helpers, ambient lights, and impostor billboards
  // are excluded as targets.
  let measureTool: PlotexMeasureTool | undefined;
  if (cfg.measureTool?.enabled !== false) {
    measureTool = new PlotexMeasureTool({
      scene,
      camera,
      renderer,
      controls,
      targetMeshes: loaded.scene,
    });
  }

  // ---- Section / cutaway plane tool --------------------------------------
  // Built after the measure tool so both can coexist in the toolbar stack.
  // The tool flips `renderer.localClippingEnabled = true` in its constructor
  // and manages `renderer.clippingPlanes` on activate/deactivate, so every
  // material (progressive, instanced, impostor) gets uniformly clipped when
  // the user drags the plane.
  let sectionTool: PlotexSectionTool | undefined;
  if (cfg.sectionTool?.enabled !== false) {
    sectionTool = new PlotexSectionTool({
      scene,
      camera,
      renderer,
      target: loaded.scene,
    });
  }

  // ---- Post-processing ---------------------------------------------------
  // Priority: explicit options > project extension > 'medium' default.
  const ppPreset: PostProcessPreset =
    cfg.postprocess?.preset ??
    (projectExt?.postprocess_preset as PostProcessPreset) ??
    'medium';
  const postProcessing = new PlotexPostProcessing(renderer, scene, camera, ppPreset);

  // ---- Resize handler ----------------------------------------------------
  const onResize = () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    postProcessing.setSize(window.innerWidth, window.innerHeight);
  };
  window.addEventListener('resize', onResize);

  // ---- Animate loop ------------------------------------------------------
  // Pre-compute cross-fade band edges once per config change so the
  // per-frame path stays a few adds + a smoothstep — the update runs at
  // 60 Hz and must stay alloc-free.
  const crossfadeBand = cfg.impostor?.crossfadeBand ?? 0.5;
  const halfBand = crossfadeBand * 0.5;
  const impostorEnabled = cfg.impostor?.enabled !== false && impostorPairs.length > 0;
  // setAnimationLoop drives rAF in flatscreen mode and the XR frame
  // callback when an immersive session is active — the same function,
  // routed appropriately by three.js. Manual requestAnimationFrame here
  // would break inside a WebXR session because the browser clamps rAF
  // to the flatscreen display refresh rate while the HMD expects frame
  // pacing off its own compositor.
  //
  // lastFrameTime seeds the per-frame dt the camera-mode switcher uses
  // for framerate-independent WASD motion. Initialize it to NaN so the
  // very first frame reports dt=0 (no teleport on startup) — subsequent
  // frames derive a proper delta from performance.now().
  let lastFrameTime = 0;
  function animate() {
    const now = performance.now();
    const dt = lastFrameTime > 0 ? (now - lastFrameTime) / 1000 : 0;
    lastFrameTime = now;

    controls.update();
    // Walk-mode integration. No-op when the switcher is in orbit mode or
    // pointer lock isn't held, so the cost is ~3 branches when inactive.
    cameraModeSwitcher.update(dt);
    lodManager.update(camera, now);

    // [plotex-hook:impostor-tick] — per-frame real/impostor swap.
    //
    // Pure distance check per pair: if the camera sits beyond
    // switchDistance + halfBand, show the impostor billboard; if closer
    // than switchDistance - halfBand, show the real geometry; in the
    // narrow crossfade band we keep both visible and fade the
    // impostor's opacity with smoothstep so the swap doesn't pop.
    //
    // We intentionally only fade the impostor, not the real mesh. The
    // real mesh's material is shared across many instances and mutating
    // `transparent` / `opacity` mid-frame would ripple through unrelated
    // draws. The impostor barely occupies ~1-2 pixels at the switch
    // distance anyway, so fading just one side is imperceptible.
    //
    // No-op shortcut: when no candidates opted in we skip the loop
    // AND the cameraWorldPos read, keeping the tick truly zero-cost.
    if (impostorEnabled) {
      const camPos = camera.position;
      for (let i = 0; i < impostorPairs.length; i++) {
        const pair = impostorPairs[i];
        // Use MIN distance across every real instance in the cluster,
        // not the centroid. Centroid-based checks fail when the user
        // walks alongside a long row of impostors: standing 3 m from
        // one palm but 100 m from the cluster midpoint would otherwise
        // swap in billboards and hide the real leaves right next to
        // you. Min-distance keeps all real geometry visible whenever
        // ANY instance is close, which is the right default for
        // walk-mode + orbital views alike. The cost is 52 squared-
        // distance comparisons per frame per pair — microseconds.
        let dSq = Infinity;
        for (let j = 0; j < pair.instancePositions.length; j++) {
          const p = pair.instancePositions[j];
          const dx = camPos.x - p.x;
          const dy = camPos.y - p.y;
          const dz = camPos.z - p.z;
          const s = dx * dx + dy * dy + dz * dz;
          if (s < dSq) dSq = s;
        }
        const d = Math.sqrt(dSq);
        const min = pair.switchDistance - halfBand;
        const max = pair.switchDistance + halfBand;
        const mat = pair.impostor.material as PlotexImpostorMaterial;
        if (d >= max) {
          // Fully swapped: impostor visible, real hidden.
          pair.real.visible = false;
          pair.impostor.visible = true;
          mat.opacity = 1;
        } else if (d <= min) {
          // Near the camera: real geometry visible, impostor hidden.
          pair.real.visible = true;
          pair.impostor.visible = false;
        } else {
          // Inside the blend band. Impostor fades in as distance grows.
          // smoothstep = t * t * (3 - 2 * t) where t is the normalized
          // position across the band. Same curve three.js / GLSL use,
          // so the visual behavior matches the material's internal
          // 8-view blend aesthetic.
          const t = (d - min) / crossfadeBand;
          const s = t * t * (3 - 2 * t);
          pair.real.visible = true;
          pair.impostor.visible = true;
          mat.opacity = s;
        }
        // Refresh camera-pos uniform so the vertex shader can recompute
        // azimuth every frame. If we skip this on hidden impostors the
        // first frame after a swap-in reads a stale uCameraPos value
        // and shows the wrong view for one tick.
        mat.updateCameraPos(camPos);
      }
    }
    // [/plotex-hook:impostor-tick]

    // [plotex-hook:measure-tick] — reposition measurement labels.
    // Cheap O(n) camera.project() per measurement; skipped entirely when
    // the tool wasn't built. Must run before the render() call so the
    // label DOM transforms reflect the frame we're about to present.
    if (measureTool) measureTool.update();
    // [/plotex-hook:measure-tick]

    // [plotex-hook:postprocess] — EffectComposer-driven rendering.
    // postProcessing.render() delegates to the composer when a preset
    // is active, or falls through to renderer.render() when 'off'.
    postProcessing.render();
    // [/plotex-hook:postprocess]
  }
  renderer.setAnimationLoop(animate);

  // [plotex-hook:xr-button] — the three.js VRButton is a self-contained
  // <button> that probes `navigator.xr` for `immersive-vr` support,
  // starts/ends sessions via `renderer.xr.setSession(...)`, and updates
  // its own label ("ENTER VR" / "EXIT VR" / "VR NOT SUPPORTED") as
  // state changes. We don't need to wire any event listeners — just
  // restyle it to match the Plotex toolbar aesthetic and stack it above
  // the measure / quality / camera-mode controls.
  //
  // We keep the button visible even when WebXR is unsupported so users
  // see the capability gap rather than guess at why VR isn't available.
  // The internal button.disabled state already prevents accidental
  // clicks on unsupported hardware.
  let vrButton: HTMLElement | null = null;
  if (xrEnabled) {
    vrButton = VRButton.createButton(renderer);
    Object.assign(vrButton.style, {
      // Stacks above camera-mode (bottom:16), measure (bottom:80/136-ish
      // depending on widget stack), and quality slider. 208 px keeps a
      // comfortable 16 px gap above the existing stack of 40-high widgets.
      position: 'fixed',
      bottom: '208px',
      right: '16px',
      left: 'auto',
      width: '120px',
      height: '40px',
      padding: '0 12px',
      borderRadius: '8px',
      border: '1px solid rgba(74, 158, 255, 0.25)',
      outline: 'none',
      cursor: 'pointer',
      background: 'rgba(15, 18, 24, 0.9)',
      color: '#e6e9ef',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '11px',
      fontWeight: '600',
      letterSpacing: '0.8px',
      textTransform: 'uppercase',
      textAlign: 'center',
      lineHeight: '38px',
      zIndex: '9999',
      transition: 'background 0.15s ease, border-color 0.15s ease',
      userSelect: 'none',
      // three.js sets opacity:0.5 when disabled — override so the disabled
      // state is distinguished by cursor + border rather than a washed-out
      // look that clashes with the rest of the toolbar.
      opacity: '1',
    });
    vrButton.addEventListener('mouseenter', () => {
      if (vrButton) {
        vrButton.style.background = 'rgba(24, 30, 42, 0.95)';
        vrButton.style.borderColor = 'rgba(74, 158, 255, 0.6)';
      }
    });
    vrButton.addEventListener('mouseleave', () => {
      if (vrButton) {
        vrButton.style.background = 'rgba(15, 18, 24, 0.9)';
        vrButton.style.borderColor = 'rgba(74, 158, 255, 0.25)';
      }
    });
    document.body.appendChild(vrButton);
  }
  // [/plotex-hook:xr-button]

  // ---- Build the engine handle -------------------------------------------
  // Wave 6 Task 5A — attach the live-sync receiver BEFORE building the
  // engine handle so dispose() can clean it up alongside the other
  // resources. attachLiveSync() returns null when we're not running on
  // localhost (prod hosts), so the private field is typed as nullable.
  let liveSync: PlotexLiveSync | null = null;

  const engine: PlotexEngine = {
    scene,
    camera,
    renderer,
    controls,
    lodManager,
    postProcessing,
    impostorPairs,
    measureTool,
    cameraModeSwitcher,
    sectionTool,
    dispose() {
      // Passing null unregisters both the rAF callback (flatscreen) and
      // the XR frame callback (immersive) — one call tears down the
      // entire animate loop regardless of session state.
      renderer.setAnimationLoop(null);
      if (vrButton) vrButton.remove();
      window.removeEventListener('resize', onResize);
      if (liveSync) liveSync.dispose();
      // Release impostor GPU resources BEFORE lodManager so the worker
      // pool doesn't accidentally reparent a texture we're about to free.
      // Each pair owns:
      //   - the quad geometry (PlaneGeometry — ours to dispose)
      //   - the material (PlotexImpostorMaterial — ours)
      //   - the atlas texture (sampled by the material's uAtlas uniform)
      // The real mesh stays alive; it's owned by `loaded.scene` and
      // lodManager will dispose its geometry when it walks its own state.
      for (const pair of impostorPairs) {
        const mat = pair.impostor.material as PlotexImpostorMaterial;
        const atlas = mat.uniforms.uAtlas?.value as Texture | undefined;
        if (atlas && typeof atlas.dispose === 'function') atlas.dispose();
        mat.dispose();
        pair.impostor.geometry.dispose();
        scene.remove(pair.impostor);
      }
      // Section tool disposes BEFORE the measure tool (reverse-alphabetical
      // stack teardown matches our construction order) so its PlaneHelper
      // is out of the scene graph before the measure tool walks its own
      // raycast filters. Also removes our clipping plane from
      // renderer.clippingPlanes before the renderer itself is disposed.
      if (sectionTool) sectionTool.dispose();
      // Measure tool disposes BEFORE lodManager so its marker / line
      // geometries are detached from the scene before the manager walks
      // through for its own mesh cleanup.
      if (measureTool) measureTool.dispose();
      // Camera-mode switcher owns a PointerLockControls instance + DOM
      // button + keyboard listeners. Disposing here (before OrbitControls
      // is torn down) ensures the switcher's "reset orbit.enabled=true"
      // safety path still has a valid controls reference to touch.
      cameraModeSwitcher.dispose();
      lodManager.dispose();
      controls.dispose();
      renderer.dispose();
    },
    setPostProcessPreset(preset: PostProcessPreset) {
      postProcessing.setPreset(preset);
    },
  };

  // On localhost we hook up the dev-server live-reload channel. This is a
  // no-op off-host (attachLiveSync returns null for prod domains), so the
  // production bundle never opens a socket or schedules reconnect timers.
  liveSync = attachLiveSync(engine);

  return engine;
}

// Re-export types consumers may need.
export type { PlotexEngineOptions } from './types';
export { resolveOptions } from './types';
export type { PostProcessPreset } from './core/PlotexPostProcessing';
